
import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Upload, Loader2, Clock, User, CalendarClock, AlertTriangle, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

interface BackupItem {
  id: string;
  created_at: string;
  team_id: string;
  version: string;
  is_automated: boolean;
  created_by: string | null;
}

interface BackupRestoreDialogProps {
  teamId: string;
  isOpen: boolean;
  onClose: () => void;
  onRestoreComplete: () => void;
}

const BackupRestoreDialog: React.FC<BackupRestoreDialogProps> = ({ 
  teamId, 
  isOpen, 
  onClose,
  onRestoreComplete
}) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [selectedBackupId, setSelectedBackupId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'manual' | 'auto'>('manual');
  const [autoBackups, setAutoBackups] = useState<BackupItem[]>([]);
  const [manualBackups, setManualBackups] = useState<BackupItem[]>([]);

  useEffect(() => {
    if (isOpen && teamId) {
      loadBackups();
    }
  }, [isOpen, teamId]);

  const loadBackups = async () => {
    if (!teamId) {
      setError(t("teamIdRequired"));
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Fetching backups for team ID:', teamId);
      
      // Fetch backups using the Edge Function
      const { data: responseData, error } = await supabase.functions.invoke('get_schedule_backups', {
        body: { team_id: teamId }
      });
      
      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message);
      }
      
      console.log('Response from edge function:', responseData);
      
      if (!responseData || !Array.isArray(responseData.data)) {
        console.error('Invalid data format received:', responseData);
        throw new Error("Invalid data format");
      }
      
      const data = responseData.data;
      console.log('Received backups data:', data);
      
      // Filter backups for this team only (should be already filtered by the edge function, but just to be safe)
      const teamBackups = data.filter((backup: BackupItem) => backup.team_id === teamId);
      
      // Separate auto and manual backups
      const auto = teamBackups.filter((backup: BackupItem) => 
        backup.is_automated === true || backup.version === 'auto-backup'
      );
      
      const manual = teamBackups.filter((backup: BackupItem) => 
        backup.is_automated !== true && backup.version !== 'auto-backup'
      );
      
      setBackups(teamBackups);
      setAutoBackups(auto);
      setManualBackups(manual);
      
      console.log("Processed backups:", { 
        total: teamBackups.length,
        auto: auto.length,
        manual: manual.length
      });
      
      if (teamBackups.length === 0) {
        setError(t("noBackupsAvailable"));
      } else {
        // Default to the tab that has backups
        if (auto.length > 0) {
          setActiveTab('auto');
        } else if (manual.length > 0) {
          setActiveTab('manual');
        }
        
        // Clear any selected backup ID since we loaded new data
        setSelectedBackupId('');
      }
    } catch (error) {
      console.error('Error loading backups:', error);
      setError(error instanceof Error ? error.message : String(error));
    } finally {
      setIsLoading(false);
    }
  };

  const triggerBackup = async () => {
    if (!teamId) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("teamIdRequired"),
      });
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      toast({
        title: t("creatingBackup") || "Creating Backup",
        description: t("backupInProgress") || "Backup in progress...",
      });
      
      // Trigger the run_hourly_backup function to create a backup now
      const { data, error } = await supabase.functions.invoke('run_hourly_backup', {
        body: { team_id: teamId, manual_trigger: true }
      });
      
      if (error) throw error;
      
      console.log('Backup result:', data);
      
      // Reload the backups list
      await loadBackups();
      
      toast({
        title: t("success") || "Success",
        description: t("backupCreated") || "Backup created successfully",
      });
    } catch (error) {
      console.error('Error triggering backup:', error);
      setError(error instanceof Error ? error.message : String(error));
      
      toast({
        variant: "destructive",
        title: t("error") || "Error",
        description: t("backupError") || "Failed to create backup",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedBackupId) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("noBackupSelected"),
      });
      return;
    }
    
    if (!teamId) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("teamIdRequired"),
      });
      return;
    }

    setIsRestoring(true);
    setError(null);
    
    try {
      console.log('Fetching backup with ID:', selectedBackupId);
      
      // Fetch the selected backup using the Edge Function
      const { data, error } = await supabase.functions.invoke('get_schedule_backup_by_id', {
        body: { backup_id: selectedBackupId }
      });
      
      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message);
      }
      
      if (!data || !data.schedules) {
        console.error('Invalid backup data:', data);
        throw new Error("Invalid backup data");
      }
      
      // Confirm with user before proceeding with restore
      if (!window.confirm(t("restoreConfirmation"))) {
        setIsRestoring(false);
        return;
      }
      
      console.log(`Restoring ${data.schedules.length} schedules`);
      
      // Process each schedule in the backup
      for (const schedule of data.schedules) {
        // Check if the schedule already exists
        const { error: upsertError } = await supabase
          .from('team_schedules')
          .upsert(schedule, { 
            onConflict: 'id',
            ignoreDuplicates: false 
          });
          
        if (upsertError) {
          console.error('Error upserting schedule:', upsertError);
        }
      }
      
      toast({
        title: t("restoreSuccessful"),
        description: t("scheduleRestored"),
      });
      
      onRestoreComplete();
      onClose();
      
      // Reset selection
      setSelectedBackupId('');
      
    } catch (error) {
      console.error('Restore error:', error);
      setError(error instanceof Error ? error.message : String(error));
      
      toast({
        variant: "destructive",
        title: t("restoreError"),
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsRestoring(false);
    }
  };

  // Format date for display
  const formatBackupDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }).format(date);
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  // Get current backup list based on active tab
  const currentBackupList = activeTab === 'auto' ? autoBackups : manualBackups;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("restoreFromBackup")}</DialogTitle>
          <DialogDescription>
            {t("selectBackupToRestore")}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              size="sm"
              onClick={loadBackups}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {t("refreshBackups")}
            </Button>
            
            <Button
              variant="secondary"
              size="sm"
              onClick={triggerBackup}
              disabled={isLoading}
            >
              <Clock className="h-4 w-4 mr-2" />
              {t("createAutoBackupNow") || "Create Auto-Backup Now"}
            </Button>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-2">{t("loadingBackups")}</span>
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <>
              <Tabs 
                value={activeTab} 
                onValueChange={(value) => {
                  setActiveTab(value as 'manual' | 'auto');
                  setSelectedBackupId('');
                }}
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="manual" disabled={manualBackups.length === 0}>
                    <User className="mr-2 h-4 w-4" />
                    {t("manualBackups")} 
                    {manualBackups.length > 0 && 
                      <Badge variant="outline" className="ml-2">{manualBackups.length}</Badge>
                    }
                  </TabsTrigger>
                  <TabsTrigger value="auto" disabled={autoBackups.length === 0}>
                    <Clock className="mr-2 h-4 w-4" />
                    {t("autoBackups")}
                    {autoBackups.length > 0 && 
                      <Badge variant="outline" className="ml-2">{autoBackups.length}</Badge>
                    }
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="manual">
                  {manualBackups.length === 0 ? (
                    <Alert>
                      <CalendarClock className="h-4 w-4" />
                      <AlertTitle>{t("noManualBackups")}</AlertTitle>
                      <AlertDescription>
                        {t("noManualBackupsDescription")}
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Select
                      disabled={isRestoring}
                      value={selectedBackupId}
                      onValueChange={setSelectedBackupId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("selectBackup")} />
                      </SelectTrigger>
                      <SelectContent className="bg-background border">
                        {manualBackups.map((backup) => (
                          <SelectItem key={backup.id} value={backup.id}>
                            {formatBackupDate(backup.created_at)} 
                            {backup.version && backup.version !== 'auto-backup' && 
                              <span className="ml-2 text-xs text-muted-foreground">({backup.version})</span>
                            }
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </TabsContent>
                
                <TabsContent value="auto">
                  {autoBackups.length === 0 ? (
                    <Alert>
                      <CalendarClock className="h-4 w-4" />
                      <AlertTitle>{t("noAutoBackups")}</AlertTitle>
                      <AlertDescription>
                        {t("noAutoBackupsDescription")}
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      <Alert variant="default" className="mb-4">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>{t("autoBackupInfo")}</AlertTitle>
                        <AlertDescription>
                          {t("autoBackupDescription") || "Automatic backups are created hourly and stored for 48 hours."}
                        </AlertDescription>
                      </Alert>
                      
                      <Select
                        disabled={isRestoring}
                        value={selectedBackupId}
                        onValueChange={setSelectedBackupId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t("selectAutoBackup")} />
                        </SelectTrigger>
                        <SelectContent className="bg-background border">
                          {autoBackups.map((backup) => (
                            <SelectItem key={backup.id} value={backup.id}>
                              <div className="flex items-center">
                                <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                                {formatBackupDate(backup.created_at)}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button 
            onClick={handleRestore} 
            disabled={!selectedBackupId || isRestoring || backups.length === 0}
            className="ml-2"
          >
            {isRestoring && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Upload className={`${isRestoring ? '' : 'mr-2'} h-4 w-4`} />
            {isRestoring ? t("restoringBackup") : t("restoreBackup")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BackupRestoreDialog;
